<template>
  <InfuseModal
    :title="isUpdate ? t(updateTitleKey) : t(createTitleKey)"
    @close="emit('close')"
    :disable-close="loading"
  >
    <form :id="formId" @submit.prevent="onSubmit" class="flex max-h-[600px] flex-col gap-4 overflow-y-auto">
      <div class="flex flex-col gap-2">
        <InfuseField v-if="showName" :label="t('common.name')">
          <InfuseInput
            :required="true"
            v-model="nameMutation"
            ref="nameInputRef"
            :placeholder="t(namePlaceholderKey)"
          />
        </InfuseField>

        <InfuseField :label="t('common.description')">
          <InfuseInput
            :required="false"
            v-model="descriptionMutation"
            ref="descriptionInputRef"
            :placeholder="t(descriptionPlaceholderKey)"
          />
        </InfuseField>

        <InfuseField v-if="showCocNumber" :label="t('iam.organisations.cocNumber')" optional>
          <InfuseInput
            v-model="cocNumberMutation"
            :placeholder="t(cocNumberPlaceholderKey || 'iam.organisations.cocNumber_placeholder')"
          />
        </InfuseField>
      </div>

      <!-- Address Fields -->
      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold">{{ t('common.address_section') }}</h3>
        <InfuseField :label="t('common.address')" optional>
          <InfuseInput v-model="addressMutation" :placeholder="t(addressPlaceholderKey)" :maxlength="255" />
        </InfuseField>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('common.postalCode')" optional class="basis-1/2">
            <InfuseInput v-model="postalCodeMutation" :placeholder="t(postalCodePlaceholderKey)" :maxlength="20" />
          </InfuseField>

          <InfuseField :label="t('common.city')" optional class="basis-1/2">
            <InfuseInput v-model="cityMutation" :placeholder="t(cityPlaceholderKey)" :maxlength="255" />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('common.country')" optional class="basis-1/2">
            <InfuseCountrySelect v-model="countryMutation" format="alpha2" />
          </InfuseField>

          <InfuseField :label="t('common.region')" optional class="basis-1/2">
            <InfuseInput v-model="regionMutation" :placeholder="t(regionPlaceholderKey)" :maxlength="255" />
          </InfuseField>
        </div>
      </div>

      <!-- Contact/Info Fields -->
      <div class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold">{{ t('common.contact_section') }}</h3>
        <InfuseField :label="t('common.website')" optional>
          <InfuseInput v-model="websiteMutation" type="url" :placeholder="t(websitePlaceholderKey)" :maxlength="255" />
        </InfuseField>

        <InfuseField :label="t('common.email')" optional>
          <InfuseInput v-model="emailMutation" type="email" :placeholder="t(emailPlaceholderKey)" :maxlength="255" />
        </InfuseField>

        <InfuseField :label="t('common.type')" optional>
          <InfuseInput v-model="typeMutation" :placeholder="t(typePlaceholderKey)" :maxlength="255" />
        </InfuseField>

        <InfuseField :label="t('common.externalId')" optional>
          <InfuseInput v-model="externalIdMutation" :placeholder="t(externalIdPlaceholderKey)" :maxlength="255" />
        </InfuseField>
      </div>
    </form>
    <template #actions>
      <InfuseButton color="secondary" type="button" @click="emit('close')" :disabled="loading">
        {{ t('action.cancel') }}
      </InfuseButton>

      <InfuseButton type="submit" :form="formId" :disabled="loading" :loading="loading">
        {{ t('action.submit') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseCountrySelect, InfuseField, InfuseInput, InfuseModal } from '@infuse/ui/components';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export interface EntityWithMutationFields {
  id?: string;
  name?: string;
  description?: string;
  cocNumber?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  region?: string;
  website?: string;
  email?: string;
  externalId?: string;
  type?: string;
}

const { t } = useI18n();

const nameMutation = ref('');
const descriptionMutation = ref<string | undefined>('');
const cocNumberMutation = ref<string | undefined>('');
const addressMutation = ref<string | undefined>('');
const postalCodeMutation = ref<string | undefined>('');
const cityMutation = ref<string | undefined>('');
const countryMutation = ref<string | undefined>('');
const regionMutation = ref<string | undefined>('');
const websiteMutation = ref<string | undefined>('');
const emailMutation = ref<string | undefined>('');
const externalIdMutation = ref<string | undefined>('');
const typeMutation = ref<string | undefined>('');

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    entity?: EntityWithMutationFields;
    showName?: boolean;
    createTitleKey: string;
    updateTitleKey: string;
    namePlaceholderKey?: string;
    descriptionPlaceholderKey: string;
    addressPlaceholderKey?: string;
    postalCodePlaceholderKey?: string;
    cityPlaceholderKey?: string;
    regionPlaceholderKey?: string;
    websitePlaceholderKey?: string;
    emailPlaceholderKey?: string;
    externalIdPlaceholderKey?: string;
    typePlaceholderKey?: string;
    showCocNumber?: boolean;
    cocNumberPlaceholderKey?: string;
  }>(),
  {
    loading: false,
    showName: true,
    namePlaceholderKey: 'common.name',
    addressPlaceholderKey: 'common.address',
    postalCodePlaceholderKey: 'common.postalCode',
    cityPlaceholderKey: 'common.city',
    regionPlaceholderKey: 'common.region',
    websitePlaceholderKey: 'common.website',
    emailPlaceholderKey: 'common.email',
    externalIdPlaceholderKey: 'common.externalId',
    typePlaceholderKey: 'common.type'
  }
);

const isUpdate = computed(() => !!props.entity);
const formId = computed(() => (isUpdate.value ? 'update-entity' : 'add-entity'));

interface Emits {
  (event: 'close'): void;
  (event: 'mutate', vars: { id: string | undefined; entity: EntityWithMutationFields }): void;
}

const emit = defineEmits<Emits>();

watch(
  () => props.entity,
  (entity) => {
    if (!entity) {
      // Reset all fields when creating new entity
      nameMutation.value = '';
      descriptionMutation.value = '';
      cocNumberMutation.value = '';
      addressMutation.value = '';
      postalCodeMutation.value = '';
      cityMutation.value = '';
      countryMutation.value = '';
      regionMutation.value = '';
      websiteMutation.value = '';
      emailMutation.value = '';
      externalIdMutation.value = '';
      typeMutation.value = '';
      return;
    }
    nameMutation.value = entity.name || '';
    descriptionMutation.value = entity.description;
    cocNumberMutation.value = entity.cocNumber;
    addressMutation.value = entity.address;
    postalCodeMutation.value = entity.postalCode;
    cityMutation.value = entity.city;
    countryMutation.value = entity.country;
    regionMutation.value = entity.region;
    websiteMutation.value = entity.website;
    emailMutation.value = entity.email;
    externalIdMutation.value = entity.externalId;
    typeMutation.value = entity.type;
  },
  { immediate: true }
);

const onSubmit = () => {
  emit('mutate', {
    id: props.entity?.id,
    entity: {
      name: nameMutation.value,
      description: descriptionMutation.value || undefined,
      cocNumber: cocNumberMutation.value || undefined,
      address: addressMutation.value || undefined,
      postalCode: postalCodeMutation.value || undefined,
      city: cityMutation.value || undefined,
      country: countryMutation.value || undefined,
      region: regionMutation.value || undefined,
      website: websiteMutation.value || undefined,
      email: emailMutation.value || undefined,
      externalId: externalIdMutation.value || undefined,
      type: typeMutation.value || undefined
    }
  });
};
</script>
